import { useState, type ReactNode } from "react";
import {
  primaryOrderLabel,
  type OpsLookupSaleRow,
  type OpsLookupUnitRow,
} from "@/lib/opsLookup/opsLookupModel";
import {
  formatOpsLookupUsd,
  type OpsLookupCostRollup,
} from "@/lib/opsLookup/opsLookupCosts";
import type { OpsLookupLocationSlot } from "@/lib/opsLookup/opsLookupLocations";
import { openOpsPaperworkDeal } from "@/lib/opsLookup/openOpsPaperworkDeal";

const btnPrimary =
  "rounded-md bg-slate-800 px-3 py-1.5 text-sm font-medium text-white hover:bg-slate-700 disabled:opacity-50 dark:bg-zinc-200 dark:text-zinc-900 dark:hover:bg-white";
const btnGhost =
  "rounded-md border border-gray-300 px-3 py-1.5 text-sm text-gray-800 hover:bg-gray-50 disabled:opacity-50 dark:border-zinc-600 dark:text-zinc-200 dark:hover:bg-zinc-800";

export function OpsLookupSaleDetail(props: {
  sale: OpsLookupSaleRow;
  linkedUnit: OpsLookupUnitRow | null;
  cost: OpsLookupCostRollup;
  costLoading: boolean;
  relatedServices?: ReactNode;
  onOpenOrder: (unitId: string) => void;
}) {
  const { sale, linkedUnit, cost, costLoading, relatedServices, onOpenOrder } = props;
  const serial = linkedUnit ? primaryOrderLabel(linkedUnit) : "—";

  return (
    <section className="space-y-3 rounded-lg border border-gray-200 p-4 dark:border-zinc-700">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <h3 className="text-base font-semibold text-gray-900 dark:text-zinc-100">
          {sale.display_name?.trim() || "Untitled sale"}
        </h3>
        <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-700 dark:bg-zinc-800 dark:text-zinc-200">
          {sale.status}
        </span>
      </div>
      <dl className="grid grid-cols-2 gap-3 text-sm">
        <div>
          <dt className="text-gray-500 dark:text-zinc-400">Linked serial / order</dt>
          <dd className="font-medium text-gray-900 dark:text-zinc-100">{serial}</dd>
        </div>
        <div>
          <dt className="text-gray-500 dark:text-zinc-400">Paperwork missing</dt>
          <dd className="font-medium text-gray-900 dark:text-zinc-100">
            {sale.paperwork_missing ?? "—"}
          </dd>
        </div>
      </dl>
      <CostRollupBlock cost={cost} loading={costLoading} />
      {relatedServices}
      <p className="text-xs text-gray-500 dark:text-zinc-400">Read-only. Edit paperwork in Operations.</p>
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          className={btnPrimary}
          disabled={!linkedUnit}
          onClick={() => linkedUnit && onOpenOrder(linkedUnit.id)}
        >
          Open linked order
        </button>
        <button type="button" className={btnGhost} onClick={() => openOpsPaperworkDeal(sale.id)}>
          Open paperwork
        </button>
      </div>
    </section>
  );
}

export function OpsLookupOrderDetail(props: {
  unit: OpsLookupUnitRow;
  plantName: string | null;
  linkedSale: OpsLookupSaleRow | null;
  cost: OpsLookupCostRollup;
  costLoading: boolean;
  locationTriad: OpsLookupLocationSlot[];
  locationsLoading: boolean;
  relatedServices?: ReactNode;
  onOpenSale: (dealId: string) => void;
}) {
  const {
    unit,
    plantName,
    linkedSale,
    cost,
    costLoading,
    locationTriad,
    locationsLoading,
    relatedServices,
    onOpenSale,
  } = props;
  const [showUuid, setShowUuid] = useState(false);

  return (
    <section className="space-y-3 rounded-lg border border-gray-200 p-4 dark:border-zinc-700">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <h3 className="text-base font-semibold text-gray-900 dark:text-zinc-100">
          {primaryOrderLabel(unit)}
        </h3>
        <span
          className={`rounded-full px-2 py-0.5 text-xs font-medium ${
            unit.serial
              ? "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200"
              : "bg-amber-100 text-amber-900 dark:bg-amber-900/40 dark:text-amber-100"
          }`}
        >
          {unit.serial ? "serialized" : "pre-serial"}
        </span>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <button type="button" className={btnGhost} onClick={() => setShowUuid((v) => !v)}>
          {showUuid ? "Hide manufacturing ID" : "Show manufacturing ID"}
        </button>
        {showUuid ? (
          <code className="text-xs text-gray-600 dark:text-zinc-400">{unit.id}</code>
        ) : null}
      </div>
      <dl className="grid grid-cols-2 gap-3 text-sm">
        <div>
          <dt className="text-gray-500 dark:text-zinc-400">Sale path</dt>
          <dd className="font-medium text-gray-900 dark:text-zinc-100">{unit.sale_path}</dd>
        </div>
        <div>
          <dt className="text-gray-500 dark:text-zinc-400">Plant</dt>
          <dd className="font-medium text-gray-900 dark:text-zinc-100">
            {plantName || unit.plant_id}
          </dd>
        </div>
      </dl>
      <div>
        <h4 className="text-sm font-semibold text-gray-900 dark:text-zinc-100">
          Locations{locationsLoading ? " (loading…)" : ""}
        </h4>
        <ul className="mt-1 space-y-1 text-sm text-gray-700 dark:text-zinc-300">
          {locationTriad.map((slot) => (
            <LocRow
              key={slot.kind}
              kind={slot.label}
              value={slot.detail}
              current={slot.isCurrent}
            />
          ))}
        </ul>
      </div>
      <CostRollupBlock cost={cost} loading={costLoading} />
      {relatedServices}
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          className={btnPrimary}
          disabled={!unit.ops_deal_id}
          onClick={() => unit.ops_deal_id && onOpenSale(unit.ops_deal_id)}
        >
          Open linked sale
        </button>
        <button
          type="button"
          className={btnGhost}
          disabled={!unit.ops_deal_id}
          onClick={() => unit.ops_deal_id && openOpsPaperworkDeal(unit.ops_deal_id)}
        >
          Open paperwork
        </button>
      </div>
      {linkedSale ? (
        <p className="text-xs text-gray-500 dark:text-zinc-400">
          Linked sale: {linkedSale.display_name?.trim() || linkedSale.id}
        </p>
      ) : null}
    </section>
  );
}

function CostRollupBlock(props: { cost: OpsLookupCostRollup; loading: boolean }) {
  const { cost, loading } = props;
  const varianceTone =
    cost.varianceUsd == null
      ? "text-gray-900 dark:text-zinc-100"
      : cost.varianceUsd > 0
        ? "text-red-700 dark:text-red-300"
        : "text-emerald-700 dark:text-emerald-300";

  return (
    <div className="space-y-2">
      <h4 className="text-sm font-semibold text-gray-900 dark:text-zinc-100">
        Cost factors{loading ? " (loading…)" : ""}
      </h4>
      <dl className="grid grid-cols-3 gap-2 text-sm">
        <div>
          <dt className="text-gray-500 dark:text-zinc-400">Theoretical</dt>
          <dd className="font-medium text-gray-900 dark:text-zinc-100">
            {formatOpsLookupUsd(cost.theoreticalUsd)}
          </dd>
        </div>
        <div>
          <dt className="text-gray-500 dark:text-zinc-400">Actual</dt>
          <dd className="font-medium text-gray-900 dark:text-zinc-100">
            {formatOpsLookupUsd(cost.actualUsd)}
          </dd>
        </div>
        <div>
          <dt className="text-gray-500 dark:text-zinc-400">Variance</dt>
          <dd className={`font-medium ${varianceTone}`}>
            {cost.varianceUsd == null
              ? "—"
              : `${cost.varianceUsd > 0 ? "+" : ""}${formatOpsLookupUsd(cost.varianceUsd)}`}
          </dd>
        </div>
        <div>
          <dt className="text-gray-500 dark:text-zinc-400">Retail</dt>
          <dd className="font-medium text-gray-900 dark:text-zinc-100">
            {formatOpsLookupUsd(cost.retailUsd)}
          </dd>
        </div>
        <div>
          <dt className="text-gray-500 dark:text-zinc-400">Dealer cost</dt>
          <dd className="font-medium text-gray-900 dark:text-zinc-100">
            {formatOpsLookupUsd(cost.dealerCostUsd)}
          </dd>
        </div>
      </dl>
      <p className="text-xs text-gray-500 dark:text-zinc-400">
        Dealer cost is explicit only (deal/snapshot metadata). Catalog wholesale
        {cost.catalogWholesaleUsd != null
          ? ` reference: ${formatOpsLookupUsd(cost.catalogWholesaleUsd)}`
          : " not mapped here"}
        . Theoretical source: {cost.theoreticalSource ?? "—"}; actual: {cost.actualSource ?? "—"}.
      </p>
    </div>
  );
}

function LocRow(props: { kind: string; value: string; current: boolean }) {
  return (
    <li className="flex flex-wrap items-baseline gap-2">
      <span className="w-16 shrink-0 font-medium">{props.kind}</span>
      <span className="min-w-0 flex-1 break-all">{props.value}</span>
      {props.current ? (
        <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200">
          current
        </span>
      ) : null}
    </li>
  );
}
